import { useDispatch } from 'react-redux';
import { TextKey, RouteName } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { Bill, BillState } from '@mews-commander/data-access';
import { useResolveRoute } from '@mews/routing';
import { useCommanderSelector } from '@mews-commander/utils';
import { EditDialogForm, useFormValueSelector } from '@mews-commander/legacy-form';
import { isDialogOpen } from '../../dialog/selectors';
import { closeDialog } from '../../dialog/reducer';
import { getBillingAccountUrl } from '../../navigation/billing';
import { openModal } from '../../app/actions/common';
import { BillManipulationMode } from '../constants';
import { DialogContent } from './DialogContent';

export const BILL_UPDATED_REVIEW_DIALOG_NAME = 'changeBillOwnerReviewDialog';

const BillUpdatedReviewDialog = () => {
    const dispatch = useDispatch();
    const active = useCommanderSelector((state) => isDialogOpen(state, { name: BILL_UPDATED_REVIEW_DIALOG_NAME }));
    const mode: BillManipulationMode = useFormValueSelector(BILL_UPDATED_REVIEW_DIALOG_NAME, 'mode');
    const fromBills: Bill[] = useFormValueSelector(BILL_UPDATED_REVIEW_DIALOG_NAME, 'fromBills');
    const toBill: Bill = useFormValueSelector(BILL_UPDATED_REVIEW_DIALOG_NAME, 'toBill');
    const oldBillName: string | undefined = useFormValueSelector(BILL_UPDATED_REVIEW_DIALOG_NAME, 'oldBillName');
    const newBillName: string | undefined = useFormValueSelector(BILL_UPDATED_REVIEW_DIALOG_NAME, 'newBillName');
    const resolveRoute = useResolveRoute();

    const accountBillingUrl = useCommanderSelector((state) => {
        if (toBill) {
            if (toBill.state === BillState.Closed) {
                return resolveRoute(RouteName.BillDetail, { id: toBill.id });
            }
            return getBillingAccountUrl(state, { id: toBill.id, accountId: toBill.ownerId });
        }
    });

    if (!active) return null;

    if (!toBill || !accountBillingUrl) {
        throw new Error(`Cannot find bill with id ${toBill?.id} or account billing url`);
    }

    const handleSubmit = () => {
        dispatch(openModal(accountBillingUrl));
        dispatch(closeDialog(BILL_UPDATED_REVIEW_DIALOG_NAME));
    };

    const title = {
        [BillManipulationMode.MoveItems]: TextKey.MoveItemsReviewDialogHeaderTitle,
        [BillManipulationMode.MoveBill]: TextKey.ChangeBillOwnerReviewDialogHeaderTitle,
    };

    const textContent = {
        [BillManipulationMode.MoveItems]: {
            fromBillTextKey: TextKey.MoveItemsReviewDialogOriginalBillHeading,
            toBillTextKey: TextKey.MoveItemsReviewDialogNewBillHeading,
        },
        [BillManipulationMode.MoveBill]: {
            toBillTextKey: TextKey.ChangeBillOwnerReviewDialogNewBillInfoHeading,
        },
    };

    return (
        <EditDialogForm
            active={active}
            size="large"
            closeDialog={() => dispatch(closeDialog(BILL_UPDATED_REVIEW_DIALOG_NAME))}
            title={<LocalizedText textKey={title[mode]} />}
            loading={false}
            primaryButtonTextKey={TextKey.ViewBill}
            handleSubmit={handleSubmit}
        >
            <DialogContent
                fromBills={fromBills}
                toBill={toBill}
                oldBillName={oldBillName}
                newBillName={newBillName}
                {...textContent[mode]}
            />
        </EditDialogForm>
    );
};

export default BillUpdatedReviewDialog;
